import { createCollaboratorNotification, CreateCollaboratorNotificationPayload } from "src/apis/collaboratorNotifications";

type CollaboratorNotificationJob = {
  id: string;
  payload: CreateCollaboratorNotificationPayload;
};

const COLLABORATOR_QUEUE_KEY = "collaboratorNotificationQueue";

let isProcessingCollaboratorQueue = false;

const loadQueue = (): CollaboratorNotificationJob[] => {
  try {
    const stored = localStorage.getItem(COLLABORATOR_QUEUE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveQueue = (queue: CollaboratorNotificationJob[]): void => {
  try {
    localStorage.setItem(COLLABORATOR_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // ignore storage errors
  }
};

const generateJobId = (): string => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const processCollaboratorQueue = async (): Promise<void> => {
  if (isProcessingCollaboratorQueue) return;
  isProcessingCollaboratorQueue = true;

  try {
    while (true) {
      const queue = loadQueue();
      if (!queue.length) break;

      const [job] = queue;

      try {
        await createCollaboratorNotification(job.payload);
      } catch {
        // fail silently to keep the queue running
      }

      const latestQueue = loadQueue();
      const filteredQueue = latestQueue.filter((entry) => entry.id !== job.id);
      saveQueue(filteredQueue);
    }
  } finally {
    isProcessingCollaboratorQueue = false;
  }
};

export const enqueueCollaboratorNotifications = (jobs: CreateCollaboratorNotificationPayload[]): void => {
  if (!jobs.length) return;

  const queue = loadQueue();
  const enriched = jobs.map((payload) => ({
    id: generateJobId(),
    payload,
  }));

  saveQueue([...queue, ...enriched]);
  void processCollaboratorQueue();
};

export const startNotificationQueueProcessing = (): void => {
  void processCollaboratorQueue();
};
